import { useLazyUser } from "$global-providers/user-provider";
import { gql } from "@apollo/client";
import type { QueryPullMyEpocusProgramsArgs } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import { toExamTypes } from "../collections/epocus-exam-worksheets";
import type {
  BillingModifierOverride,
  EpocusProgram,
  ProgramClinicalSetting,
  WorksheetSet,
} from "../collections/epocus-program";
import type { RxDBCollections } from "../provider";
import {
  SyncEpocusProgramPullDocument,
  type WholeSyncableEpocusProgramFragment,
} from "./__generated__/epocus-program.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import {
  EPOCUS_PROGRAM_PULL_ROLES,
  isReplicationRoleAllowed,
} from "./replication-role-allowlist";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

const PROGRAM_CHECKPOINT_ENTITY = "global-billing-setting";

export const useReplicateEpocusProgram = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<EpocusProgram>;
}) => {
  const role = useLazyUser()?.role;
  const allowed = isReplicationRoleAllowed(EPOCUS_PROGRAM_PULL_ROLES, role);

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncableEpocusProgramFragment>
  >(
    () => ({
      query: SyncEpocusProgramPullDocument,
      reSyncInterval: { minutes: 30 },
      ...createPersistedCheckpointPullBuilders<
        QueryPullMyEpocusProgramsArgs,
        WholeSyncableEpocusProgramFragment
      >({
        buildInputVariables: (checkpoint, batchSize) => ({
          input: {
            limit: batchSize,
            checkpoint,
          },
        }),
        buildPullCheckpoint: (doc) => ({
          id: doc.id,
          updatedAt: doc.meta.updatedAt,
        }),
        entityId: PROGRAM_CHECKPOINT_ENTITY,
        db,
        collectionName: collection?.name,
      }),
    }),
    [db, collection],
  );

  return useGqlReplication<
    EpocusProgram,
    WholeSyncableEpocusProgramFragment,
    null
  >({
    db,
    collection,
    pull: allowed ? pull : undefined,
    mapGqlToRx: mapGqlEpocusProgramToRx,
    batchSize: 100,
    expireData: false,
    metadataLocalStorageKeySuffix: "epocus-program",
    enabled: allowed,
  });
};

export const mapGqlEpocusProgramToRx = (
  doc: WholeSyncableEpocusProgramFragment,
): WithDeleted<EpocusProgram> => ({
  id: doc.id,
  name: doc.name,
  status: doc.status.toLowerCase() as EpocusProgram["status"],
  elephantFacilityId: doc.elephantFacilityId ?? null,
  dimseAeTitle: doc.dimseAeTitle ?? null,
  turnaroundTargetMinutes: doc.turnaroundTargetMinutes ?? null,
  enabledExamTypes: toExamTypes(doc.enabledExamTypes),
  worksheetSet: doc.worksheetSet.toLowerCase() as WorksheetSet,
  clinicalSetting: doc.clinicalSetting.toLowerCase() as ProgramClinicalSetting,
  billingModifierOverride: (doc.billingModifierOverride?.toLowerCase() ??
    null) as BillingModifierOverride | null,
  enabledDeviceIds: [...doc.enabledDeviceIds],
  meta: {
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedByUserId: doc.meta.updatedByUserId,
  },
  _deleted: doc.meta.deleted,
});

gql`
  fragment WholeSyncableEpocusProgram on SyncableEpocusProgram {
    id
    name
    status
    elephantFacilityId
    dimseAeTitle
    turnaroundTargetMinutes
    enabledExamTypes
    worksheetSet
    clinicalSetting
    billingModifierOverride
    enabledDeviceIds
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncEpocusProgramPull($input: PullMyEpocusProgramsInput!) {
    pullMyEpocusPrograms(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableEpocusProgram
      }
    }
  }
`;
